"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";
import { AnimatedStats } from "../WhyChooseUsSection/AnimatedStats";

interface SuccessResultsClientProps {
  data: {
    content: {
      title?: string;
      testimonial?: {
        quote?: string;
        author?: string;
        title?: string;
      };
      stats?: Array<{
        id: number;
        label: string;
        value: number | null;
        suffix: string | null;
        description: string | null;
        animation: {
          start: number;
          end: number;
        };
      }>;
      image?: {
        src: string;
        alt: string;
      };
    };
  };
}

export default function SuccessResultsClient({
  data,
}: SuccessResultsClientProps) {
  const { title, testimonial, stats, image } = data.content;

  
  const sectionRef = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const testimonialRef = useRef<HTMLQuoteElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  
  useEffect(() => {
    const sectionElement = sectionRef.current;
    const imageElement = imageRef.current;
    const titleElement = titleRef.current;
    const testimonialElement = testimonialRef.current;
    const statsElement = statsRef.current;

    if (!sectionElement) return;

    
    gsap.set([imageElement, titleElement, testimonialElement, statsElement], {
      opacity: 0,
    });

    gsap.set(imageElement, {
      y: 40,
      scale: 0.95,
      filter: "blur(8px)",
    });

    gsap.set(titleElement, {
      y: 30,
      filter: "blur(8px)",
    });

    gsap.set(testimonialElement, {
      y: 20,
      filter: "blur(8px)",
    });

    gsap.set(statsElement, {
      y: 20,
      filter: "blur(8px)",
    });

    const ctx = gsap.context(() => {
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionElement,
          start: "top 80%",
          end: "top 40%",
          toggleActions: "play none none reverse",
        },
      });

      
      tl.to(imageElement, {
        opacity: 1,
        y: 0,
        scale: 1,
        filter: "blur(0px)",
        duration: 0.8,
        ease: "power2.out",
      })
        .to(
          titleElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.4"
        )
        .to(
          testimonialElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.6"
        )
        .to(
          statsElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.4"
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-[70px] relative bg-theme-secondary">
      <div className="container">
        {}
        {image && image.src && (
          <div
            ref={imageRef}
            className="relative w-full h-[300px] md:h-[390px] max-h-[400px] overflow-hidden mb-12 opacity-0"
          >
            <Image
              src={image.src}
              alt={image.alt || "Success Results"}
              fill
              quality={85}
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 80vw, 1200px"
              className="object-center object-cover size-full"
              priority
            />
          </div>
        )}

        {}
        <div className="text-[#F5F5F5]">
          {}
          {title && (
            <h2
              ref={titleRef}
              className="text-[28px] md:text-4xl lg:text-5xl font-bold text-white mb-8 max-w-4xl leading-tight opacity-0"
            >
              {title}
            </h2>
          )}

          {}
          {testimonial && (
            <blockquote
              ref={testimonialRef}
              className="border-l-4 border-orange-500 pl-6 mb-8 opacity-0"
            >
              {testimonial.quote && (
                <p className="text-[18px] md:text-[20px] leading-[28px] font-semibold max-w-[580px] text-[#F5F5F5]">
                  &quot;{testimonial.quote}&quot;
                </p>
              )}
              {(testimonial.author || testimonial.title) && (
                <cite className="block mt-4 text-[14px] md:text-[15.125px] font-medium leading-[24px] not-italic text-[#CFD1D2]">
                  - {testimonial.author}
                  {testimonial.title && `, ${testimonial.title}`}
                </cite>
              )}
            </blockquote>
          )}

          {}
          {stats && stats.length > 0 && (
            <div ref={statsRef} className="opacity-0">
              <AnimatedStats
                stats={stats}
                className="text-[#CFD1D2] font-semibold"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
